/**
 * Motion tokens — single source of truth for every duration, easing,
 * distance, scale and blur used by UI transitions (CSS) and scene tweens (TS).
 *
 * The scale is the transitions.dev motion-token scale
 * (https://transitions.dev — `skills/transitions-polish/_root.css`, also
 * vendored under `.agents/skills/transitions-polish/`). Tokens marked
 * `source: "transitions.dev"` keep the upstream name and value so the
 * `transitions review` / `transitions polish` skills can audit this project
 * against the same grid. Tokens marked `source: "project"` extend the scale
 * with deliberate house rules that the upstream set has no slot for:
 *
 *   - `duration/hover-in`  0s   — hover feedback lands on the next paint
 *                                 (see `instantHover.css`); only hover-OUT
 *                                 eases. Do NOT let a polish pass "fix" this.
 *   - `duration/reduced-motion` 1ms — the `prefers-reduced-motion` override
 *                                 every module applies (`transitionend` still
 *                                 fires; `0s` would not).
 *   - `ease/standard`, `ease/sheet` — the two non-upstream curves the app
 *                                 already relies on (Material standard for
 *                                 collapse/fade choreography, iOS-sheet for
 *                                 drawers and the mobile bottom sheet).
 *   - `duration/camera-flight`, `duration/sun-sweep` — Three.js scene tweens.
 *
 * Consumption:
 *   - In CSS: `motionTokensCss()` is injected once at the app root by
 *     `DesignTokensStyle`, so any stylesheet can reference
 *     `var(--duration-fast)`, `var(--ease-smooth-out)`, …
 *   - In TS: `motionTokens.duration.fast.ms`, `motionTokens.ease.smoothOut.value`,
 *     or the numeric helpers below.
 *
 * Keep this the ONLY place these values live. Never write a literal `ms`
 * duration or `cubic-bezier(...)` in a stylesheet when a token's *usage*
 * matches (match on usage, not on the nearest number — a 300ms modal close
 * is still `--duration-quick`). Values whose usage matches no token (long
 * marketing reveals, spinner periods, the 1.5s clip-path wipe) stay literal.
 */

export type MotionGroup = "duration" | "ease" | "distance" | "scale" | "blur";

export type MotionTokenSource = "transitions.dev" | "project";

export interface MotionToken {
  /** Stable identity, e.g. "duration/fast". */
  readonly path: string;
  /** Dimension bucket. */
  readonly group: MotionGroup;
  /** Leaf name within the group, e.g. "fast". */
  readonly name: string;
  /** CSS custom property, e.g. "--duration-fast". */
  readonly cssVar: string;
  /** CSS value, e.g. "250ms", "cubic-bezier(0.22, 1, 0.36, 1)", "8px", "0.96". */
  readonly value: string;
  /** Where the motion should be used. This — not the raw number — decides a match. */
  readonly usage: string;
  /** Upstream token or a project extension. */
  readonly source: MotionTokenSource;
  /** Optional caveat. */
  readonly note?: string;
}

/** A duration token additionally exposes its value in milliseconds for TS tweens. */
export interface DurationToken extends MotionToken {
  readonly group: "duration";
  readonly ms: number;
}

type TokenSeed = {
  value: string;
  usage: string;
  source: MotionTokenSource;
  note?: string;
};

type GroupSeed = Record<string, TokenSeed>;

const T = "transitions.dev" as const;
const P = "project" as const;

/**
 * Raw token values, grouped by dimension. This is the editable source;
 * everything else in this file is derived from it. Upstream tokens are in
 * transitions.dev order; project extensions follow within each group.
 */
const SEED = {
  duration: {
    stagger: { value: "40ms", usage: "per-item stagger offset", source: T },
    micro: {
      value: "80ms",
      usage: "tooltip/path delay, shake segment, large stagger, press-down transform",
      source: T
    },
    quick: {
      value: "150ms",
      usage: "modal/dropdown close, text swap, tooltip appear, hover-out color fade",
      source: T
    },
    fast: {
      value: "250ms",
      usage: "icon swap, dropdown/modal open, tabs sliding, page slide, backdrop fade",
      source: T
    },
    medium: {
      value: "350ms",
      usage: "panel close, toast close, sheet slide, content height tween",
      source: T
    },
    slow: {
      value: "400ms",
      usage: "panel open, skeleton content reveal, input clear, collapse region",
      source: T
    },
    "very-slow": {
      value: "500ms",
      usage: "emphasis moments, badge appear, text reveal, success check",
      source: T
    },
    "hover-in": {
      value: "0s",
      usage: "hover feedback IN — instant, lands on the next paint",
      source: P,
      note: "House rule from instantHover.css: the 3D canvas eats frames, so an eased hover-in reads as lag. Only hover-out fades."
    },
    "reduced-motion": {
      value: "1ms",
      usage: "prefers-reduced-motion override for every transition/animation",
      source: P,
      note: "1ms, not 0s, so transitionend still fires for JS that waits on it."
    },
    "camera-flight": {
      value: "600ms",
      usage: "Three.js camera preset flight (position + target lerp)",
      source: P
    },
    "sun-sweep": {
      value: "2800ms",
      usage: "boot-time sun sweep from sunrise to the current time",
      source: P
    }
  },
  ease: {
    "smooth-out": {
      value: "cubic-bezier(0.22, 1, 0.36, 1)",
      usage: "modal/dropdown/panel open + close, page slide, resize, position change",
      source: T,
      note: "Default surface easing (easeOutQuint). JS twin: easeSmoothOut()."
    },
    "in-out": {
      value: "ease-in-out",
      usage: "icon swap, text swap, text reveal, skeleton reveal, shimmer",
      source: T
    },
    out: {
      value: "ease-out",
      usage: "tooltip open / close, backdrop fade, hover-out color fade",
      source: T
    },
    linear: {
      value: "linear",
      usage: "shimmer, skeleton pulse, spinner, visibility gating",
      source: T
    },
    bounce: {
      value: "cubic-bezier(0.34, 1.36, 0.64, 1)",
      usage: "badge pop open",
      source: T
    },
    "bounce-strong": {
      value: "cubic-bezier(0.34, 3.85, 0.64, 1)",
      usage: "bouncy hover-out (avatar return)",
      source: T
    },
    standard: {
      value: "cubic-bezier(0.4, 0, 0.2, 1)",
      usage: "collapse-region choreography, chip/toggle fades, overlay scrim",
      source: P,
      note: "Material 'standard' curve. Symmetric-ish; used where a reversible fold needs the same feel both ways."
    },
    sheet: {
      value: "cubic-bezier(0.32, 0.72, 0, 1)",
      usage: "bottom sheet / drawer slide, segmented-control pill, viewer chrome slide-in",
      source: P,
      note: "iOS UISheetPresentation-style: quick start, long settle."
    }
  },
  distance: {
    micro: { value: "4px", usage: "text swap", source: T },
    small: { value: "6px", usage: "error shake (small segment)", source: T },
    base: {
      value: "8px",
      usage: "badge diagonal reveal, page slide, error shake (large segment)",
      source: T
    },
    medium: { value: "12px", usage: "text reveal", source: T },
    large: { value: "30px", usage: "check badge appear", source: T }
  },
  scale: {
    large: { value: "0.96", usage: "modal open / close", source: T },
    medium: { value: "0.97", usage: "dropdown open", source: T },
    small: { value: "0.98", usage: "tooltip open", source: T },
    tiny: { value: "0.99", usage: "dropdown close", source: T }
  },
  blur: {
    small: {
      value: "2px",
      usage: "panel reveal, icon swap, text swap, skeleton reveal, number pop-in",
      source: T
    },
    medium: { value: "3px", usage: "page slide, text reveal", source: T },
    large: { value: "8px", usage: "success check open", source: T }
  }
} satisfies Record<MotionGroup, GroupSeed>;

type Seed = typeof SEED;

function parseMs(value: string): number {
  const m = /^(-?\d*\.?\d+)(ms|s)$/.exec(value.trim());
  if (!m) throw new Error(`motionTokens: not a CSS <time>: "${value}"`);
  const n = Number.parseFloat(m[1]);
  return m[2] === "s" ? n * 1000 : n;
}

function toToken(group: MotionGroup, name: string, seed: TokenSeed): MotionToken {
  const base: MotionToken = {
    path: `${group}/${name}`,
    group,
    name,
    cssVar: `--${group}-${name}`,
    value: seed.value,
    usage: seed.usage,
    source: seed.source,
    ...(seed.note ? { note: seed.note } : {})
  };
  if (group === "duration") {
    const d: DurationToken = { ...base, group: "duration", ms: parseMs(seed.value) };
    return d;
  }
  return base;
}

/**
 * Structured, grouped token map. Typed so `motionTokens.duration.fast.ms`
 * and `motionTokens.ease["smooth-out"].value` autocomplete.
 */
export const motionTokens = Object.fromEntries(
  Object.entries(SEED).map(([group, groupSeed]) => [
    group,
    Object.fromEntries(
      Object.entries(groupSeed).map(([name, seed]) => [
        name,
        toToken(group as MotionGroup, name, seed)
      ])
    )
  ])
) as {
  [G in MotionGroup]: {
    [N in keyof Seed[G]]: G extends "duration" ? DurationToken : MotionToken;
  };
};

/** Flat list of every token, in seed order. Handy for iteration (showcase, audits). */
export const MOTION_TOKENS: readonly MotionToken[] = Object.values(motionTokens).flatMap(
  (group) => Object.values(group) as MotionToken[]
);

/** Group display order. */
export const MOTION_TOKEN_GROUPS: readonly MotionGroup[] = Object.keys(SEED) as MotionGroup[];

/** Tokens for a single group, in order. */
export function tokensForGroup(group: MotionGroup): readonly MotionToken[] {
  return Object.values(motionTokens[group]) as MotionToken[];
}

/** Look up a token by its path (e.g. "duration/fast"). */
export function tokenByPath(path: string): MotionToken | undefined {
  return MOTION_TOKENS.find((t) => t.path === path);
}

/**
 * JS twin of `--ease-smooth-out` (`cubic-bezier(0.22, 1, 0.36, 1)` is the
 * canonical easeOutQuint approximation). Use for rAF tweens that should
 * feel like the CSS surface transitions.
 */
export function easeSmoothOut(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - c, 5);
}

/**
 * Generate the CSS custom-property declarations. Injected once at the app
 * root by `DesignTokensStyle`, alongside the color and typography tokens.
 * Motion tokens do not vary by theme, so a single `:root` block suffices.
 */
export function motionTokensCss(): string {
  const lines: string[] = [":root {"];
  for (const group of MOTION_TOKEN_GROUPS) {
    lines.push(`  /* ${group} */`);
    for (const t of tokensForGroup(group)) {
      lines.push(`  ${t.cssVar}: ${t.value};`);
    }
  }
  lines.push("}");
  return lines.join("\n");
}
